import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';
import { giftAdmin, type GiftCardSettings, type GiftHealth } from '@/lib/giftCards';

export default function GiftSettingsPanel({
  settings,
  onSaved,
  health,
}: {
  settings: GiftCardSettings;
  onSaved: (s: GiftCardSettings) => void;
  health: GiftHealth | null;
}) {
  const [draft, setDraft] = useState<GiftCardSettings>(settings);
  const [presets, setPresets] = useState((settings.presets_ils ?? []).join(', '));
  const [occasions, setOccasions] = useState((settings.occasions ?? []).join(', '));
  const [busy, setBusy] = useState(false);

  const set = <K extends keyof GiftCardSettings>(key: K, value: GiftCardSettings[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const save = async () => {
    const presetList = presets
      .split(',')
      .map((p) => Number(p.trim()))
      .filter((n) => Number.isFinite(n) && n > 0);
    const occasionList = occasions
      .split(',')
      .map((o) => o.trim())
      .filter(Boolean);
    if (Number(draft.min_amount_ils) <= 0 || Number(draft.max_amount_ils) <= Number(draft.min_amount_ils)) {
      return toast.error('טווח הסכומים אינו תקין');
    }
    setBusy(true);
    try {
      const res = await giftAdmin<{ settings: GiftCardSettings }>('settings.save', {
        patch: {
          public_sale_enabled: draft.public_sale_enabled,
          presets_ils: presetList,
          min_amount_ils: Number(draft.min_amount_ils),
          max_amount_ils: Number(draft.max_amount_ils),
          allow_custom_amount: draft.allow_custom_amount,
          expiry_months: draft.expiry_months === null ? null : Number(draft.expiry_months),
          occasions: occasionList,
          logo_url: draft.logo_url?.trim() || null,
          bg_style: draft.bg_style,
          accent: draft.accent,
          headline_he: draft.headline_he,
          terms_he: draft.terms_he,
          sender_name: draft.sender_name,
          reply_to: draft.reply_to?.trim() || null,
          gift_product_variant_gid: draft.gift_product_variant_gid?.trim() || null,
        },
      });
      onSaved(res.settings);
      setDraft(res.settings);
      toast.success('ההגדרות נשמרו');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'השמירה נכשלה');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="border rounded-xl bg-white p-4 space-y-4">
        <div className="font-medium">סכומים וכללים</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">סכומים מוצעים (מופרדים בפסיק)</Label>
            <Input value={presets} onChange={(e) => setPresets(e.target.value)} dir="ltr" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">סכום מינימלי (₪)</Label>
            <Input
              value={String(draft.min_amount_ils)}
              onChange={(e) => set('min_amount_ils', Number(e.target.value) as any)}
              inputMode="numeric"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">סכום מקסימלי (₪)</Label>
            <Input
              value={String(draft.max_amount_ils)}
              onChange={(e) => set('max_amount_ils', Number(e.target.value) as any)}
              inputMode="numeric"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">תוקף בחודשים (ריק = ללא תפוגה)</Label>
            <Input
              value={draft.expiry_months === null ? '' : String(draft.expiry_months)}
              onChange={(e) => set('expiry_months', (e.target.value === '' ? null : Number(e.target.value)) as any)}
              inputMode="numeric"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">אירועים (מופרדים בפסיק)</Label>
            <Input value={occasions} onChange={(e) => setOccasions(e.target.value)} />
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <div className="text-sm">סכום חופשי</div>
              <div className="text-xs text-muted-foreground">מאפשר ללקוח להזין סכום משלו</div>
            </div>
            <Switch checked={draft.allow_custom_amount} onCheckedChange={(v) => set('allow_custom_amount', v)} />
          </div>
        </div>
      </div>

      <div className="border rounded-xl bg-white p-4 space-y-4">
        <div className="font-medium">עיצוב השובר</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">כותרת</Label>
            <Input value={draft.headline_he} onChange={(e) => set('headline_he', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">רקע</Label>
            <select
              className="w-full h-10 rounded-md border bg-white px-3 text-sm"
              value={draft.bg_style}
              onChange={(e) => set('bg_style', e.target.value)}
            >
              <option value="cream">שמנת</option>
              <option value="bark">חום עמוק</option>
              <option value="linen">פשתן</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">גוון הדגשה</Label>
            <select
              className="w-full h-10 rounded-md border bg-white px-3 text-sm"
              value={draft.accent}
              onChange={(e) => set('accent', e.target.value)}
            >
              <option value="gold">זהב</option>
              <option value="terracotta">טרקוטה</option>
            </select>
          </div>
          <div className="space-y-1.5 md:col-span-3">
            <Label className="text-xs text-muted-foreground">קישור ללוגו במייל ובעמוד השובר</Label>
            <Input value={draft.logo_url ?? ''} onChange={(e) => set('logo_url', e.target.value)} dir="ltr" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">תנאי שימוש (טקסט חופשי שמוצג בשובר)</Label>
          <Textarea value={draft.terms_he} onChange={(e) => set('terms_he', e.target.value)} rows={4} />
        </div>
      </div>

      <div className="border rounded-xl bg-white p-4 space-y-4">
        <div className="font-medium">שליחה ומכירה מקוונת</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">שם השולח/ת במייל</Label>
            <Input value={draft.sender_name} onChange={(e) => set('sender_name', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">כתובת לתשובות</Label>
            <Input value={draft.reply_to ?? ''} onChange={(e) => set('reply_to', e.target.value)} dir="ltr" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label className="text-xs text-muted-foreground">
              Variant GID של מוצר שובר בשופיפיי (נדרש למכירה מקוונת)
            </Label>
            <Input
              value={draft.gift_product_variant_gid ?? ''}
              onChange={(e) => set('gift_product_variant_gid', e.target.value)}
              dir="ltr"
              placeholder="gid://shopify/ProductVariant/123456789"
            />
          </div>
          <div className="flex items-center justify-between rounded-md border p-3 md:col-span-2">
            <div>
              <div className="text-sm">מכירת שוברים באתר</div>
              <div className="text-xs text-muted-foreground">
                מפעיל את עמוד /gift-card לרכישה עצמאית. דורש Variant GID תקין.
              </div>
            </div>
            <Switch checked={draft.public_sale_enabled} onCheckedChange={(v) => set('public_sale_enabled', v)} />
          </div>
        </div>
      </div>

      <div className="border rounded-xl bg-white p-4 space-y-2 text-sm">
        <div className="font-medium">מצב מערכת</div>
        <StatusLine ok={!!health?.shopifyGiftCards.read} label="הרשאת שופיפיי read_gift_cards" />
        <StatusLine ok={!!health?.shopifyGiftCards.write} label="הרשאת שופיפיי write_gift_cards" />
        <StatusLine
          ok={!!health?.shopifyGiftCards.read_transactions}
          label="הרשאת שופיפיי read_gift_card_transactions"
        />
        <StatusLine
          ok={!!health?.shopifyGiftCards.write_transactions}
          label="הרשאת שופיפיי write_gift_card_transactions (נדרש לחיוב/זיכוי ידני)"
        />
        {health?.shopifyGiftCards.message ? (
          <p className="text-xs text-amber-800">{health.shopifyGiftCards.message}</p>
        ) : null}
        <StatusLine ok={!!health?.pepper} label="מפתח אבטחה GIFT_CARD_SECURITY_PEPPER (נדרש לעמודי השובר הציבוריים)" />
        <StatusLine ok={!!health?.publicSiteUrl} label="כתובת האתר PUBLIC_SITE_URL (נדרשת ליצירת קישורים אישיים)" />
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-slate-400" />
          <span>חשיפת קוד מלא בעמוד הציבורי</span>
          <span className="text-xs text-muted-foreground">מושבתת עד להפעלת אימות בקוד חד-פעמי</span>
        </div>
        <StatusLine ok={!!health?.encryption} label="מפתח הצפנה GIFT_CARD_ENCRYPTION_KEY" />
        <StatusLine
          ok={!!health?.email}
          label={
            health?.emailProvider === 'google_workspace'
              ? `שליחת מייל דרך Google Workspace (Gmail)${health?.emailSender ? ` — ${health.emailSender}` : ''}`
              : health?.emailProvider === 'resend'
                ? `שליחת מייל דרך Resend${health?.emailSender ? ` — ${health.emailSender}` : ''}`
                : 'שליחת מייל לא מוגדרת — נדרש חיבור Gmail (הרשאת gmail.send) או RESEND_API_KEY'
          }
        />

        <p className="text-xs text-muted-foreground pt-1">
          סנכרון אוטומטי: הפונקציה gift-card-sync מוכנה לקריאה מתוזמנת. עד להגדרת תזמון, השתמשו בכפתור
          «סנכרון יתרות» בראש העמוד.
        </p>
      </div>

      <div className="flex">
        <Button onClick={save} disabled={busy} className="gap-2">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} שמירת הגדרות
        </Button>
      </div>
    </div>
  );
}

function StatusLine({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`h-2 w-2 rounded-full ${ok ? 'bg-emerald-500' : 'bg-amber-500'}`} />
      <span className={ok ? '' : 'text-amber-800'}>{label}</span>
      <span className="text-xs text-muted-foreground">{ok ? 'מוגדר' : 'לא מוגדר'}</span>
    </div>
  );
}
